import { useEffect, useMemo, useState } from 'react';

import { TestStatus } from '@dbt-labs/dbt-dag';

import { IconLink, PublicResourceIconLink } from '@/components/ResourceLinkList';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { useChildren, useParents } from './ancestry';
import { asFqn, getAppliedModel, useGenericResource } from './discovery/types';
import { useLinkToExplorer } from './links';
import { ProjectScope } from './params';
import { useDependentProjects } from './useDependentProjects';
import { useTargetEnvironmentType } from './useTargetEnvironmentType';

type RelationLinksParams = {
  uniqueId: ParsedUniqueId | null;
  projectScope: ProjectScope;
  onlyReferencedByProjects: boolean;
  pause?: boolean;
};

export const useRelationshipLinks = ({
  uniqueId,
  projectScope,
  onlyReferencedByProjects,
  pause,
}: RelationLinksParams) => {
  const resourceQuery = useGenericResource(uniqueId, { pause });
  const fqn = asFqn(resourceQuery.data?.fqn);
  const { dbtCloudProjects, accountId } = useDbtCloudAccount();
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const children = useChildren(resourceQuery);
  const parents = useParents(resourceQuery.data?.uniqueId);
  const hasZeroChildren = children?.length === 0;
  const hasZeroParents = parents?.length === 0;

  const { environmentId: producerEnvironmentId } = useDbtCloudProject();

  const publicModel = getAppliedModel(resourceQuery.data);
  const isPublicModel = publicModel?.access === 'public';

  const [activeTab, setActiveTab] = useState<
    'children' | 'parents' | 'referencedByProjects'
  >('parents');

  const dependentProjectsQuery = useDependentProjects(
    producerEnvironmentId,
    !isPublicModel || pause,
  );
  const dependentProjects =
    dependentProjectsQuery.data?.[uniqueId?.raw ?? '']?.children;

  const isFetching = resourceQuery.fetching || dependentProjectsQuery.fetching;

  useEffect(() => {
    if (onlyReferencedByProjects) {
      setActiveTab('referencedByProjects');
      return;
    }

    if (
      hasZeroChildren &&
      hasZeroParents &&
      isPublicModel &&
      dependentProjects?.length
    ) {
      setActiveTab('referencedByProjects');
    } else if (hasZeroChildren && !hasZeroParents) {
      setActiveTab('parents');
    } else if (!hasZeroChildren && hasZeroParents) {
      setActiveTab('children');
    }
  }, [
    hasZeroChildren,
    hasZeroParents,
    isPublicModel,
    dependentProjects?.length,
    onlyReferencedByProjects,
  ]);

  const getResourceLink = useLinkToExplorer('resourceDetails');
  const linkToLineage = useLinkToExplorer('projectLineage');

  const testStatusMap = useMemo(() => {
    const testStatusMap: Record<string, TestStatus | undefined> = {};
    resourceQuery.data?.tests?.forEach((test) => {
      const status = test.executionInfo.lastRunStatus as TestStatus | null;
      if (!status || !test.uniqueId) return;
      testStatusMap[test.uniqueId] = status;
    }, {});
    return testStatusMap;
  }, [resourceQuery.data?.tests]);

  const referencedByProjects = useMemo(() => {
    if (!dependentProjects?.length) return undefined;
    if (dbtCloudProjects === undefined) {
      return [];
    }
    return dependentProjects
      .map((project) => {
        const name = project.dbtCloudProject;
        const fqnStr = fqn?.join('.');
        return {
          name,
          href:
            fqnStr && dbtCloudProjects.some((proj) => proj.id === project.projectId)
              ? linkToLineage({
                  accountId: accountId || 0,
                  projectId: project?.projectId || 0,
                  environmentType: getTargetEnvironmentType(
                    project?.environmentId ?? 0,
                  ),
                  dbtUniqueId: uniqueId?.raw,
                  environmentId: producerEnvironmentId,
                })
              : null,
          isInternal: true,
          resourceType: 'project' as const,
          isReferenced: (project.numDependentModels ?? 0) > 0,
        };
      })
      .filter((project) => project.isReferenced);
  }, [
    accountId,
    dbtCloudProjects,
    dependentProjects,
    fqn,
    getTargetEnvironmentType,
    linkToLineage,
    producerEnvironmentId,
    uniqueId?.raw,
  ]);

  // TODO: could we enforce stronger typing so that it guarantees it's a PublicResourceIconLink if onlyreferencedByProjects is true?
  // (instead of `(IconLink | PublicResourceIconLink)[]`)
  // UPDATE: We gave a crack at it but did not arrive to a solution without typing errors.
  // It's not a must-do TODO, but in the spirit of stronger typing, we can revisit this in the future
  const links = useMemo((): (IconLink | PublicResourceIconLink)[] | undefined => {
    switch (activeTab) {
      case 'children':
        const referencedByResources = children?.map((uniqueId) => ({
          name: uniqueId.remainder,
          href: getResourceLink({
            ...projectScope,
            dbtUniqueId: uniqueId.raw,
          }),
          isInternal: true,
          resourceType: uniqueId.resourceType,
          status: testStatusMap[uniqueId.raw],
        }));

        return [...(referencedByProjects || []), ...(referencedByResources || [])];
      case 'parents':
        return parents?.map((uniqueId) => ({
          name: uniqueId.remainder,
          href: getResourceLink({
            ...projectScope,
            dbtUniqueId: uniqueId.raw,
          }),
          isInternal: true,
          resourceType: uniqueId.resourceType,
        }));
      case 'referencedByProjects':
        return referencedByProjects;
    }
  }, [
    activeTab,
    children,
    parents,
    referencedByProjects,
    getResourceLink,
    projectScope,
    testStatusMap,
  ]);

  return useMemo(() => {
    return {
      links,
      isFetching,
      parents,
      children,
      activeTab,
      setActiveTab,
      isPublicModel,
    };
  }, [links, isFetching, parents, children, activeTab, isPublicModel]);
};
